<script setup>
import { ref, defineEmits, defineProps, watch } from 'vue';
import { $api } from '@/services';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const router = useRouter();
const store = useStore();
const name = ref('');
const error = ref('');
const emit = defineEmits(['close', 'submit', 'reloadData']);
const props = defineProps({
	id: {
		type: String,
		required: true,
	},
	surveyTemplateName: {
		type: String,
		required: false,
	},
	type: {
		type: String,
		required: false,
		default: 'duplicate',
	},
	status: {
		type: String,
		required: false,
		default: 'draft',
	},
	surveyName: {
		type: String,
		required: false,
		default: '',
	},
});

const syncNameFromProps = () => {
	if (props.type === 'create') {
		name.value = props.surveyName ?? '';
		return;
	}
	if (props.type === 'update') {
		name.value = props.surveyTemplateName ?? '';
		return;
	}
	if (props.type === 'duplicate') {
		name.value = 'Bản sao của ' + props.surveyTemplateName ?? '';
		return;
	}
};

watch(
	() => [props.type, props.surveyName, props.surveyTemplateName],
	syncNameFromProps,
	{ immediate: true }
);

const submit = async () => {
	if (props.type === 'exit') {
		emit('submit');
		return;
	}
	if (props.type === 'deleteTemplate') {
		const res = await $api.survey.deleteTemplate(props.id);
		if (res) {
			await store.dispatch('setAlertMessage', {
				message: 'Xoá biểu mẫu thành công',
				type: 'success',
			});
			emit('close');
			emit('reloadData');
		}
		return;
	}
	if (props.type === 'recall') {
		const res = await $api.survey.recallSurvey(props.id);
		if (res) {
			await store.dispatch('setAlertMessage', {
				message: 'Thu hồi phiếu khảo sát thành công',
				type: 'success',
			});
			emit('success');
		}
		return;
	}
	if (props.type === 'create') {
		try {
			const res = await $api.survey.createTemplate(props.id, {
				survey_template: {
					name: name.value,
				},
			});
			if (res) {
				await store.dispatch('setAlertMessage', {
					message: 'Tạo biểu mẫu thành công',
					type: 'success',
				});
				router.push('/survey?tab=template&tabSelected=center');
			}
		} catch (err) {
			if (err?.response?.data?.errors_by_field['name']) {
				error.value = 'Tên biểu mẫu không được trùng với tên đã có';
				setTimeout(() => {
					error.value = '';
				}, 3000);
			}
		}
		return;
	}
	if (props.type === 'deleteSurvey') {
		const res = await $api.survey.deleteSurvey(props.id);
		if (res) {
			await store.dispatch('setAlertMessage', {
				message: 'Xoá phiếu khảo sát thành công',
				type: 'success',
			});
			router.push('/survey');
		}
		return;
	}
	if (props.type === 'duplicate') {
		try {
			const res = await $api.survey.duplicateTemplate(props.id, {
				survey_template: {
					name: name.value,
				},
			});
			if (res) {
				await store.dispatch('setAlertMessage', {
					message: 'Tạo bản sao thành công',
					type: 'success',
				});
				emit('close');
				emit('reloadData');
			}
		} catch (err) {
			if (err?.response?.data?.errors_by_field['name']) {
				error.value =
					'Tên biểu mẫu ' +
					err?.response?.data?.errors_by_field['name'].join(' ');
				setTimeout(() => {
					error.value = '';
				}, 3000);
			}
		}
		return;
	}
	if (props.type === 'update') {
		try {
			const res = await $api.survey.updateSurveyTemplate(props.id, {
				survey_template: {
					name: name.value,
				},
			});
			if (res) {
				await store.dispatch('setAlertMessage', {
					message: 'Đổi tên biểu mẫu thành công',
					type: 'success',
				});
				emit('close');
				emit('reloadData');
				return;
			}
		} catch (err) {
			if (err?.response?.data?.errors_by_field['name']) {
				error.value = 'Tên biểu mẫu không được trùng với tên đã có';
				setTimeout(() => {
					error.value = '';
				}, 3000);
			}
		}
	}
	// const res = await $api.survey.updateSurvey(props.id, {
	// 	survey: {
	// 		name: name.value,
	// 	},
	// });
	// if (res) {
	// 	emit('close');
	// }
};
</script>
<template>
	<div class="_create-boarding _modal bg-white">
		<div
			class="_modal__content px-12 py-6 flex flex-col items-center justify-center"
		>
			<img
				v-if="props.type === 'duplicate'"
				class="w-16 h-16"
				alt="Luca Education"
				src="@/assets/images/illustrations/duplicate.png"
				title="Luca Education"
			/>
			<img
				v-else-if="
					props.type === 'exit' ||
					props.type === 'deleteTemplate' ||
					props.type === 'recall'
				"
				class="w-16 h-16"
				alt="Luca Education"
				src="@/assets/images/illustrations/warning.png"
				title="Luca Education"
			/>
			<img
				v-else
				class="w-16 h-16"
				alt="Luca Education"
				src="@/assets/images/illustrations/edit.png"
				title="Luca Education"
			/>
			<div
				class="_modal__content__title text-neutral-700 font-semibold text-2xl mt-5 text-center"
			>
				{{
					props.type === 'duplicate'
						? 'Tạo bản sao'
						: props.type === 'update'
						? 'Đổi tên biểu mẫu'
						: props.type === 'recall'
						? 'Bạn chắc chắn muốn thu hồi phiếu khảo sát này?'
						: props.type === 'deleteTemplate'
						? 'Xóa biểu mẫu'
						: props.type === 'resend'
						? 'Gửi lại khảo sát'
						: props.type === 'deleteSurvey'
						? 'Bạn có muốn xoá phiếu khảo sát này?'
						: props.type === 'create'
						? 'Lưu thành biểu mẫu của trung tâm?'
						: props.type === 'exit'
						? 'Bạn có chắc chắn muốn thoát?'
						: ''
				}}
			</div>
			<div class="text-neutral-500 mt-2 text-center">
				{{
					props.type === 'deleteTemplate'
						? 'Biểu mẫu này sẽ được xoá khỏi dữ liệu của cơ sở.'
						: props.type === 'recall'
						? 'Phiếu khảo sát này đã được gửi đến phụ huynh. Nếu bạn thu hồi phiếu khảo sát này thì nó cũng được xoá trên app Phụ huynh.'
						: props.status === 'published' &&
						  props.type === 'deleteSurvey'
						? 'Phiếu khảo sát này đã được gửi đến phụ huynh. Khi xoá, phiếu sẽ bị xoá khỏi app phụ huynh và dữ liệu của cơ sở. '
						: props.type === 'deleteSurvey'
						? 'Phiếu khảo sát này sẽ được xoá khỏi dữ liệu của cơ sở.'
						: props.type === 'create'
						? 'Bạn có muốn lưu Phiếu khảo sát này để tái sử dụng cho những lần gửi sau không?'
						: props.type === 'exit'
						? 'Phiếu khảo sát bạn đang soạn chưa được lưu. Bạn có muốn lưu bản nháp trước khi thoát?'
						: 'Tên thư mục mới không được trùng với tên thư mục đã có trước đó.'
				}}
			</div>
		</div>
		<div
			class="px-12 pb-12"
			v-if="
				props.type !== 'deleteTemplate' &&
				props.type !== 'deleteSurvey' &&
				props.type !== 'resend' &&
				props.type !== 'recall' &&
				props.type !== 'exit'
			"
		>
			<div class="flex gap-3">
				<div
					class="text-neutral-400 font-semibold text-xs whitespace-nowrap uppercase mt-3"
				>
					Tên biểu mẫu
				</div>
				<div class="w-full input-group relative">
					<input
						type="text"
						name="name"
						id="name"
						v-model="name"
						:class="error ? 'text-error' : ''"
					/>
					<div v-if="error" class="text-sienna-600 text-sm absolute top-10 left-0">
						{{ error }}
					</div>
				</div>
			</div>
		</div>
		<div
			class="_modal__footer px-12 py-6 bg-neutral-50 flex flex-nowrap items-center"
		>
			<button
				v-if="props.type === 'exit'"
				class="button-p text-center mr-3 bg-none shadow-none text-neutral-500 hover:text-neutral-700"
				type="button"
				@click="$router.push('/survey')"
			>
				Thoát không lưu
			</button>
			<button
				v-else
				class="button-p text-center mr-3 bg-none shadow-none text-neutral-500 hover:text-neutral-700"
				type="button"
				@click="$emit('close')"
			>
				{{
					props.type === 'deleteTemplate' || props.type === 'recall'
						? 'Huỷ'
						: 'Thoát'
				}}
			</button>

			<button
				class="button-p text-center whitespace-nowrap"
				:disabled="
					!name &&
					props.type !== 'deleteTemplate' &&
					props.type !== 'recall' &&
					props.type !== 'deleteSurvey' &&
					props.type !== 'resend' &&
					props.type !== 'exit'
				"
				type="button"
				@click="submit"
			>
				{{ props.type === 'exit' ? 'Lưu bản nháp' : 'Xác nhận' }}
			</button>
		</div>
	</div>
</template>
